// ═══════════════════════════════════════════════════════════
// useAudioLevels — la onda lee el audio real
// ═══════════════════════════════════════════════════════════
//
// Saca 56 amplitudes del audio que suena y las publica en un ref, para que
// `Transport` las pinte. No dibuja nada: separar el motor del pintado permite
// montar la onda en el banco de pruebas sin que suene nada.
//
// **Qué es y qué no es.** Es un vúmetro de espectro: cada banda es la energía
// que hay AHORA en ese rango de frecuencia, leída del mismo punto de la señal
// que va a los altavoces, así que no hay desfase con lo que se oye. No es un
// seguidor de compás: no estima tempo ni fase, y es deliberado. Una onda que
// late sobre una rejilla predicha deja de reflejar lo que suena — en un pasaje
// ambiental seguiría golpeando en el vacío.
//
// ── Probado, no supuesto (25.08.26) ──
//
// Reproduciendo la emisión real en Chrome headless y midiendo la energía:
// sonando, media 51.7 sobre 255 con desviación 22.8 y picos a 91; en pausa,
// media 1.4 y cayendo. Un movimiento sintético habría dado lo mismo en los dos
// casos.
//
// ── 🔴 La trampa que dejó el analizador en cero ──
//
// `MediaElementAudioSourceNode` toma la señal **después** del volumen del
// elemento. Con `el.volume = 0.2` el analizador ve la señal ya escalada; con
// 0.0001, los bytes redondean a cero y la onda se queda plana **sin ningún
// error en consola**. Parece un fallo de CORS y no lo es.
//
// Por eso el volumen del elemento se fuerza a 1 y el volumen real se controla
// con la ganancia del grafo, que va DESPUÉS del analizador. Quien use esto no
// debe tocar `el.volume`: debe llamar a `setVolume()`.
//
// ── Otras tres que dejan la radio muda y parecen del stream ──
//
//   · `createMediaElementSource` solo admite UNA llamada por elemento. La
//     segunda lanza y el audio se queda mudo, porque el elemento ya está
//     enrutado. Por eso el nodo se cachea por elemento.
//   · Enrutar por Web Audio desconecta los altavoces hasta que se conecta al
//     `destination`. Si se olvida, no suena nada.
//   · El AudioContext nace suspendido hasta que hay un gesto del usuario. Se
//     reanuda en el play, no al montar.
//
// ── ⏱️ El arranque no es inmediato, y no es un fallo ──
//
// La emisión llega por HLS, así que el elemento reproduce desde un `blob` con
// Media Source. Medido el 25.08.26: tras pulsar play el analizador devuelve
// ceros unos diez segundos —el búfer— y luego entra la señal de golpe
// (energía 59, pico 252). Diez segundos de onda plana se leen como avería, así
// que mientras no haya señal la onda se queda en su forma de reposo en vez de
// aplanarse a cero. Eso lo decide `Transport`, que es quien pinta.
//
// Y funciona porque `listen.astra.fm` refleja el Origin que se le manda
// —comprobado—, así que con `crossOrigin="anonymous"` el elemento no queda
// «manchado». Sin esa cabecera esto devolvería silencio absoluto.

import { ref, shallowRef, onUnmounted } from "vue";

export const BAND_COUNT = 56;

// Un elemento de audio solo admite un MediaElementSource en toda su vida.
const graphs = new WeakMap();

// ── ¿Se puede leer el audio en este navegador? ──
//
// `esperando` · `si` · `no`.
//
// 🔴 **Compartido entre todas las instancias, a propósito.** Cada vista monta su
// propio `useAudioLevels`, pero esto no es un dato de la vista: es un hecho del
// navegador. Con una copia por instancia, la del hero podía quedarse en
// `esperando` para siempre —si su bucle no llegaba a arrancar— mientras otra ya
// sabía que no había señal, y el medidor no se apagaba nunca. Pasó en Safari.
//
// Se decide MIDIENDO, no mirando el user agent: el día que WebKit entregue las
// muestras, esto se pone en `si` solo.
// ⚠️ **En WebKit se arranca en `no`, sin esperar.** Safari —de escritorio y de
// iPhone, y con él todos los navegadores de iOS, que por obligación usan su
// motor— no entrega las muestras al grafo. Medido el 29.08.26: `AudioContext`
// en `running`, analizador conectado, audio entrando por Media Source y pico
// 0.000 constante.
//
// Antes se empezaba en `esperando` también aquí, y el resultado era una onda
// congelada durante los doce segundos de la comprobación. Encargo de Pablo:
// que no se muestre desde el principio.
//
// 🔴 **Sigue sin ser una lista de navegadores.** Es una presunción, y la
// medición manda sobre ella: si llega una sola muestra, esto pasa a `si` y la
// onda aparece. El día que WebKit lo arregle, se corrige solo.
//
// `navigator.vendor` dice «Apple Computer, Inc.» en Safari y en todo iOS, y
// «Google Inc.» en Chrome. Es la forma más corta de preguntar «¿esto es
// WebKit?» sin ponerse a leer cadenas de agente de usuario.
const esWebKit =
  typeof navigator !== "undefined" && /apple/i.test(navigator.vendor || "");

const analisis = ref(esWebKit ? "no" : "esperando");

// Doce segundos sonando sin un solo pico. Medido: en Chrome la señal entra sobre
// los diez, así que este umbral no se alcanza nunca cuando funciona. Empieza a
// contar en el primer `start()` de cualquier instancia.
const ESPERA_MS = 12000;
let desdeCuando = 0;

export function useAudioLevels() {
  const levels = ref([]);
  const active = ref(false);
  const supported = ref(true);

  // El estado del AudioContext, publicado para poder verlo desde fuera. En
  // Safari es la diferencia entre una onda viva y una congelada: el contexto
  // nace `suspended` y solo se reanuda si el `resume()` cae dentro del gesto
  // del usuario. Sin este dato, un análisis en cero es indistinguible de un
  // audio que no pasa por el grafo.
  const estado = ref("sin contexto");


  const ctx = shallowRef(null);
  const analyser = shallowRef(null);
  const gain = shallowRef(null);
  const source = shallowRef(null);
  const media = shallowRef(null);
  let bins = null;
  // Vigilante: fotogramas seguidos en silencio y último intento de reanimar.
  let mudo = 0;
  let ultimoIntento = 0;
  // Cuántas reanimaciones seguidas no han servido de nada. Con el grafo
  // huérfano de verdad, ninguna sirve.
  let intentosEnVano = 0;
  let yaAvisado = false;
  let ultimoTiempo = -1;
  let estabaEnPausa = false;
  let env = new Float32Array(BAND_COUNT);
  let raf = 0;

  function connect(el) {
    if (!el) return false;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) {
        supported.value = false;
        return false;
      }

      const cached = graphs.get(el);
      if (cached) {
        ctx.value = cached.ctx;
        estado.value = cached.ctx.state;
        analyser.value = cached.analyser;
        gain.value = cached.gain;
        source.value = cached.src;
        media.value = cached.el;
        bins = cached.bins;
        return true;
      }

      const context = new AudioCtx();
      const node = context.createAnalyser();
      // 512 bandas de análisis para 56 barras: forma suficiente, coste
      // despreciable.
      node.fftSize = 1024;
      // Poco suavizado. Con 0.72 la onda se aplana y los golpes se pierden;
      // el ataque rápido de abajo hace el resto del trabajo.
      node.smoothingTimeConstant = 0.5;

      const volume = context.createGain();

      // El volumen del elemento NO se toca: escala lo que ve el analizador.
      el.volume = 1;
      const src = context.createMediaElementSource(el);
      src.connect(node);
      node.connect(volume);
      volume.connect(context.destination);

      ctx.value = context;
      estado.value = context.state;
      analyser.value = node;
      gain.value = volume;
      source.value = src;
      media.value = el;
      bins = new Uint8Array(node.frequencyBinCount);
      graphs.set(el, {
        ctx: context,
        analyser: node,
        gain: volume,
        src,
        el,
        bins,
      });
      return true;
    } catch (error) {
      // CORS, navegador sin Web Audio, o elemento ya enrutado. La onda se
      // queda en reposo y la emisión sigue sonando: nunca al revés.
      supported.value = false;
      return false;
    }
  }

  function read() {
    if (!analyser.value || !bins) return;
    analyser.value.getByteFrequencyData(bins);

    // 🔴 La cuenta atrás arranca AQUÍ, en la primera lectura, y no en `start()`.
    //
    // Estaba en `start()` y en Safari no llegaba a fijarse: esa función tiene un
    // `await ctx.resume()` antes, y si esa promesa no resuelve, el resto no se
    // ejecuta. El bucle sí corría —el panel leía sus 56 bandas— pero el
    // cronómetro seguía a cero, así que la decisión no se tomaba nunca y el
    // medidor no se apagaba. Aquí no hay nada que pueda quedarse a medias: si
    // se está leyendo, se está contando.
    if (!desdeCuando) desdeCuando = performance.now();

    // Reparto logarítmico. Con bandas iguales toda la energía cae en las
    // primeras barras y las otras cuarenta no se mueven nunca: el oído es
    // logarítmico y la onda tiene que parecerse a lo que se oye.
    // ¿Ha llegado algo? Sirve al vigilante de abajo.
    let pico = 0;
    for (let i = 0; i < bins.length; i++) if (bins[i] > pico) pico = bins[i];
    if (pico > 0) {
      mudo = 0;
      // Volvió a llegar señal: lo que hubiera pasado, se arregló.
      intentosEnVano = 0;
      yaAvisado = false;
      // Y queda dicho que aquí SÍ se puede analizar. Una sola muestra basta:
      // si ha llegado una, el camino existe. Vale también para corregir la
      // presunción de WebKit: llega señal, se pinta la onda.
      if (analisis.value !== "si") analisis.value = "si";
    } else {
      mudo += 1;
      // Doce segundos desde el arranque sin una sola muestra: este navegador no
      // entrega el audio al grafo. Se dice para que quien pinte pueda enseñar
      // otra cosa en vez de una onda congelada.
      if (
        analisis.value === "esperando" &&
        desdeCuando &&
        performance.now() - desdeCuando > ESPERA_MS
      ) {
        analisis.value = "no";
      }
    }

    const top = bins.length * 0.72; // por encima solo hay aire
    const out = new Array(BAND_COUNT);
    for (let i = 0; i < BAND_COUNT; i++) {
      const from = Math.floor(top ** (i / BAND_COUNT));
      const to = Math.max(from + 1, Math.floor(top ** ((i + 1) / BAND_COUNT)));
      let sum = 0;
      for (let j = from; j < to && j < bins.length; j++) sum += bins[j];
      const value = sum / (to - from) / 255;

      // Ataque instantáneo, caída lenta. Es lo que hace que un golpe se lea
      // como golpe: si la barra sube y baja a la misma velocidad, el bombo se
      // pierde entre fotogramas y la onda tiembla en vez de marcar.
      env[i] = value > env[i] ? value : env[i] * 0.86;
      out[i] = env[i];
    }
    levels.value = out;
  }

// ── 🔴 El vigilante ──
//
// El grafo se queda mudo y no se recupera solo. Pasa cuando el player llama a
// `audioPlayer.load()` al detectar un corte, o cuando hls.js hace
// `recoverMediaError()`: las dos cosas reinician el elemento por debajo, y el
// `MediaElementAudioSourceNode` —que solo se puede crear UNA vez por elemento,
// así que no se puede rehacer— se queda enganchado a un flujo que ya no existe.
//
// El síntoma es el peor posible: la emisión se oye perfectamente y la onda se
// queda plana para siempre, sin un solo error en consola. Aparece justo al
// cambiar de tema o al entrar una locución, que es cuando hay discontinuidad.
//
// Se detecta por contraste: el elemento avanza —no está en pausa y su
// `currentTime` crece— pero el analizador lleva segundos leyendo cero. Eso no
// es silencio de la música: es el grafo desconectado.
//
// La reanimación es reconectar la cadena y reanudar el contexto. No siempre
// basta, así que se intenta como mucho cada cinco segundos en vez de en cada
// fotograma, que dejaría el hilo principal peleando consigo mismo.
function revivir() {
  const ahora = performance.now();
  if (ahora - ultimoIntento < 5000) return;
  ultimoIntento = ahora;
  intentosEnVano += 1;

  // Tres intentos con el audio corriendo y el analizador en cero: el grafo no
  // está tocado, está muerto. Reconectar no puede arreglarlo —el nodo quedó
  // atado a un flujo que ya no existe y no se puede rehacer— así que lo único
  // útil es DECIRLO, una vez, para no dejar el fallo mudo: la radio se sigue
  // oyendo y no hay ningún error que lo explique.
  if (intentosEnVano === 3 && !yaAvisado) {
    yaAvisado = true;
    console.warn(
      "[onda] El grafo de análisis quedó huérfano y no se puede rehacer: " +
        "algo reinició el elemento de audio (load(), recoverMediaError() o un " +
        "cambio de src). La emisión no está afectada; la onda sí, hasta recargar.",
    );
  }

  try {
    if (ctx.value?.state === "suspended") ctx.value.resume();
    if (source.value && analyser.value) {
      source.value.disconnect();
      source.value.connect(analyser.value);
    }
  } catch (error) {
    // Si tampoco se puede reconectar, la onda se queda en reposo. Nunca al
    // revés: la emisión no se toca.
  }
}

  function vigilar() {
    const el = media.value;
    if (!el) return;

    // El momento crítico es la vuelta: el corte para el elemento, el player lo
    // reengancha y el audio suena otra vez. Si el grafo quedó tocado, es
    // exactamente aquí donde hay que intentar reanimarlo — esperar tres
    // segundos de silencio dejaría la onda muerta un rato después de que la
    // música ya se oiga.
    if (el.paused) {
      estabaEnPausa = true;
      mudo = 0;
      return;
    }
    if (estabaEnPausa) {
      estabaEnPausa = false;
      ultimoIntento = 0; // permite reanimar sin esperar el margen de 5s
      revivir();
    }

    const avanza = el.currentTime !== ultimoTiempo;
    ultimoTiempo = el.currentTime;

    // ~3 segundos a 60fps. Un silencio real de la música dura menos que eso en
    // una emisión de radio; tres segundos planos con el audio corriendo no.
    if (mudo > 180 && avanza) revivir();
  }

  function loop() {
    read();
    vigilar();
    raf = requestAnimationFrame(loop);
  }

  async function start(el) {
    if (!connect(el)) return false;
    if (ctx.value.state === "suspended") {
      // Solo funciona dentro del gesto que dispara el play.
      try {
        await ctx.value.resume();
      } catch (error) {
        estado.value = ctx.value.state;
        return false;
      }
    }
    estado.value = ctx.value.state;
    if (!raf) loop();
    active.value = true;
    // Desde aquí se cuenta la espera. Solo la primera vez: si ya se sabe la
    // respuesta, no hay nada que volver a decidir.
    return true;
  }

  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    active.value = false;
    env = new Float32Array(BAND_COUNT);
    levels.value = [];
    mudo = 0;
    ultimoTiempo = -1;
    estabaEnPausa = false;
    intentosEnVano = 0;
    yaAvisado = false;
  }

  // El volumen se controla aquí, nunca en el elemento.
  function setVolume(value) {
    if (!gain.value) return;
    gain.value.gain.value = Math.min(Math.max(value, 0), 1);
  }

  onUnmounted(() => {
    stop();
    // El AudioContext no se cierra: el elemento sigue enrutado por él mientras
    // exista, y cerrarlo dejaría la emisión muda.
  });

  return { levels, active, supported, estado, analisis, start, stop, setVolume };
}
